import type { BlogPostViewModel } from "@/models/blog";
import { Link } from "react-router-dom";
import { Card, IconWrapper, Typography } from "@/components/ui/atoms";
import { ArticleBody } from "../article-body";
import { ArticleHeader } from "../article-header";
import { ArticleTemplate } from "../article-layout";
import { ContentsNav } from "../contents-nav";
import { CoverMedia } from "../cover-media";
import { ArticleFaq } from "../faq";
import { RelatedList } from "../related-list";
import { extractMarkdownToc, getBlogPrimaryImage } from "../../utils";
import { getButtonClasses } from "@/utils/button.utils";
import { FileQuestion } from "lucide-react";

export function BlogPostArticle({
  post,
  relatedPosts,
}: {
  post: BlogPostViewModel;
  relatedPosts: BlogPostViewModel[];
}) {
  const coverImage = getBlogPrimaryImage(post);

  return (
    <ArticleTemplate
      header={<ArticleHeader post={post} />}
      media={
        coverImage ? (
          <CoverMedia src={coverImage} alt={post.coverImageAlt || post.title} />
        ) : undefined
      }
      body={<ArticleBody markdown={post.body} />}
      afterBody={
        post.faq && post.faq.length > 0 ? <ArticleFaq faqs={post.faq} /> : null
      }
      sidebar={
        <>
          <ContentsNav items={extractMarkdownToc(post.body)} />
          <RelatedList posts={relatedPosts} currentSlug={post.slug} />
        </>
      }
    />
  );
}

export function BlogPostMissing() {
  return (
    <div className="min-h-[80vh] flex items-center justify-center py-10 sm:py-14">
      <Card variant="custom" rounded="xl" className="max-w-md mx-auto text-center">
        <div className="flex justify-center">
          <IconWrapper size="xl" color="purple" ariaHidden>
            <FileQuestion className="h-10 w-10" strokeWidth={1.5} />
          </IconWrapper>
        </div>
        <Typography variant="h4" color="primary" className="mt-4">
          We couldn&apos;t find this article
        </Typography>
        <Typography as="p" variant="body-sm" color="muted" className="mt-2">
          The post may have been moved or unpublished. Head back to the blog to keep
          reading.
        </Typography>
        <Link
          to="/blog"
          className={getButtonClasses("contained", "primary", "sm", false, "mt-6 no-underline")}
        >
          Back to blog
        </Link>
      </Card>
    </div>
  );
}
